<script setup>
import PrematchSportsNavigation from '@/components/PrematchSportsNavigation.vue';
import Carousel from '@/components/App/Carousel.vue';
import MainEventCard from './Partials/MainEventCard.vue';
import Spinner from '@/components/Spinner.vue';
import MainLeagues from './Partials/MainLeagues.vue';
import FunPlayModal from './Partials/FunPlayModal.vue';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref , watch} from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useGamesStore } from '@/stores/games';
import { useRouter } from 'vue-router';
import { useFixturesStore } from '@/stores/fixtures';
import { normalizeLang } from '@/helpers';
import JackpotCounter from '@/components/JackpotCounter.vue';

const auth = useAuthStore()
const games = useGamesStore()
const fixtures = useFixturesStore()
const assetsUrl = import.meta.env.VITE_ASSETS_URL

const { getCredits, getCurrency, getUser, isAuth, getJpTiers, getJpTiersLoading } = storeToRefs(auth)
const { getRecents , getNewGames, getGame} = storeToRefs(games)
const { getMainFixtures } = storeToRefs(fixtures)
const router = useRouter()
const opening = ref(false)
const selectedGame = ref(null)
const modalRef = ref(null)
const activeJackpotIndex = ref(0)

const jackpotsList = computed(() => getJpTiers.value || [])

const activeJackpot = computed(() => {
  const list = jackpotsList.value
  const idx = activeJackpotIndex.value
  return list[idx] || null
})

const activeJackpotTiers = computed(() => {
  const j = activeJackpot.value
  const amounts = { grand: 0, major: 0, minor: 0, mini: 0 }
  const visible = { grand: false, major: false, minor: false, mini: false }
  const hot = { grand: false, major: false, minor: false, mini: false }
  const drop = { grand: false, major: false, minor: false, mini: false }
  if (!j || !Array.isArray(j.tiers)) return { amounts, visible, hot, drop }
  for (const tier of j.tiers) {
    const name = (tier.tier_name || '').toLowerCase()
    const isReady = !!tier.is_ready
    const isDrop = !!tier.drop
    if (name === 'grand') {
      amounts.grand = tier.contribution_amount ?? 0
      visible.grand = true
      hot.grand = isReady
      drop.grand = isDrop
    } else if (name === 'major') {
      amounts.major = tier.contribution_amount ?? 0
      visible.major = true
      hot.major = isReady
      drop.major = isDrop
    } else if (name === 'minor') {
      amounts.minor = tier.contribution_amount ?? 0
      visible.minor = true
      hot.minor = isReady
      drop.minor = isDrop
    } else if (name === 'mini') {
      amounts.mini = tier.contribution_amount ?? 0
      visible.mini = true
      hot.mini = isReady
      drop.mini = isDrop
    }
  }
  return { amounts, visible, hot, drop }
})

const tabLabel = (jackpot) => {
  const cur = getCurrency.value(jackpot.currency_id)
  return cur?.cod || jackpot.name || 'Jackpot'
}

onMounted(() => {
   fixtures.fetchMainCompetitions()
   fixtures.fetchMainFixtures()
    if(isAuth.value){
        games.fetchRecentsGames() 
    }
})

const filteredGames = computed(() => {
  const recents = getRecents.value;
  if (recents.length >= 12) {
    return recents.slice(0, 12);
  }
  const needed = 12 - recents.length;
  const extras = getNewGames.value.slice(0, needed);
  return [...recents, ...extras];
});
const lang = ref(normalizeLang(localStorage.getItem('lang')))

const openGame = (game) => {
    if(isAuth.value){
        if (game.has_demo === 'Y' && getUser.value?.c_p_demo) {
            selectedGame.value = game
            modalRef.value.openModal()
        } else {
            launchGame(game, false)
        }
    }else{
         router.push({ name: 'login' })
    }

}
const launchGame = async (game, isDemo) => {
  opening.value = true;

  try {
    const res = await games.openGame(game._id, {
      device: 'M',
      currency_id: getCredits.value[0].cuid._id,
      currency_name: getCurrency.value(getCredits.value[0].cuid._id).cod,
      homeURL: import.meta.env.VITE_APP_URL,
      demo: isDemo,
      lang: lang.value
    });

    opening.value = false;

    if (res.data?.nt === true) {
      window.open(res.data.data, '_blank');
      return;
    }

    if (res.data?.nt === false) {
      games.setGameUrl(res.data.data);
      router.push('/casino/game');
    }

  } catch (error) {
    opening.value = false;
    console.error(error);
  }
};


const handlePlay = ({ demo }) => {
    launchGame(selectedGame.value, demo)
}

const sliderImages = [
    { src: '/cas11.png', url: '/casino' },
    { src: '/egt.jpg', url: '/casino/provider/6797a05c68687d3ecd919e6f' },
    { src: '/elk-studios.jpg', url: '/casino/provider/675954a1ab3ab07722b78c44' },
    { src: '/3-oaks.jpg', url: '/casino/provider/68a87a59cb4907c65bee6499' },
    { src: '/novamatic.jpg', url: '/casino/provider/6759554aab3ab07722b78ca6' },
    { src: '/pragmatic.jpg', url: '/casino/provider/67584aa2792b47680fe03982' },
    // { src: '/aviator.gif', url: '/casino' },
]
</script>

<template>
      <div class="xl:flex flex-1 overflow-hidden">
        <div class="flex-1 w-full">
            <div class="pt-1.5 px-3">
                <Carousel
                    height="150px"
                    :items="sliderImages"
                    :slidesPerView="1"
                    :spaceBetween="20"
                    :showNavigation="false"
                    :showPagination="true"
                    :autoplay="false"
                    :swiperOptions="{ loop: true }"
                >
                    <template #slide="{ slide }">
                        <RouterLink :to="slide.url" class="block w-full h-full">
                            <img :src="slide.src" alt="" class="carousel-img rounded-lg cursor-pointer"/>
                        </RouterLink>
                    </template>
                </Carousel>
            </div>
            <PrematchSportsNavigation class="py-2.5 px-3"/>    
            <div class="px-3" v-if="getMainFixtures(1).length">
                <Carousel
                    height="250px"
                    :items="getMainFixtures(1)"
                    :slidesPerView="1"
                    :spaceBetween="20"
                    :showNavigation="true"
                    :showPagination="true"
                    :autoplay="false"
                    :swiperOptions="{ loop: true }"
                >
                    <template #slide="{ slide }">
                        <MainEventCard :event="slide" />
                    </template>
                </Carousel>
            </div>
            <div v-if="getJpTiersLoading || jackpotsList.length" class="jackpot-section">
                <template v-if="getJpTiersLoading">
                    <div class="w-full flex flex-col items-center gap-3 py-8 px-2">
                        <div class="h-14 w-full max-w-[950px] rounded-lg bg-white/10 animate-pulse" />
                        <div class="h-12 w-[92%] max-w-[850px] rounded-lg bg-white/10 animate-pulse" />
                    </div>
                </template>
                <template v-else>
                    <div v-if="jackpotsList.length > 1" class="casino-jackpot-tabs">
                        <ul class="casino-jackpot-tabs__list">
                            <li
                                v-for="(jp, idx) in jackpotsList"
                                :key="jp._id"
                                role="button"
                                tabindex="0"
                                @click="activeJackpotIndex = idx"
                                @keydown.enter.space.prevent="activeJackpotIndex = idx"
                                :class="['casino-jackpot-tabs__pill', activeJackpotIndex === idx && 'casino-jackpot-tabs__pill--active']"
                            >
                                <span>{{ tabLabel(jp) }}</span>
                            </li>
                        </ul>
                    </div>
                    <JackpotCounter
                        v-if="activeJackpot"
                        :grand-jackpot="activeJackpotTiers.amounts.grand"
                        :major-jackpot="activeJackpotTiers.amounts.major"
                        :minor-jackpot="activeJackpotTiers.amounts.minor"
                        :mini-jackpot="activeJackpotTiers.amounts.mini"
                        :visible-tiers="activeJackpotTiers.visible"
                        :grand-hot="activeJackpotTiers.hot.grand"
                        :major-hot="activeJackpotTiers.hot.major"
                        :minor-hot="activeJackpotTiers.hot.minor"
                        :mini-hot="activeJackpotTiers.hot.mini"
                        :grand-drop="activeJackpotTiers.drop.grand"
                        :major-drop="activeJackpotTiers.drop.major"
                        :minor-drop="activeJackpotTiers.drop.minor"
                        :mini-drop="activeJackpotTiers.drop.mini"
                        :auto-increment="false"
                    />
                </template>
            </div>
            <div class="px-3 pb-4 block" v-if="filteredGames.length">
                <div class="flex items-center justify-between pb-2">
                    <p class="flex items-center gap-1 font-futura text-lg text-[var(--color-primary4)] uppercase font-bold">
                        <div class="star-icn" />
                        {{ $t('app.35') }}
                    </p>
                    <RouterLink 
                        :to="`/casino`"  
                        class="show-btn" 
                    >
                        <span>{{  $t('app.36') }}</span>
                        <div class="mgc-Chevron " />
                    </RouterLink>
                </div>
                <div class="relative">
                    <div class="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8  gap-2">
                        <div v-for="item in filteredGames">
                            <picture @click="openGame(item)">
                                <!-- <source 
                                    v-if="item.img"
                                    :srcset="`${assetsUrl}/c/g/${item.img}`" 
                                    type="image/webp"
                                />

                                <source 
                                    v-if="item.img"
                                    :srcset="`${assetsUrl}/c/g/${item.img}`" 
                                    type="image/jpeg"
                                /> -->
                                <img 
                                    :src="item.img ? `${assetsUrl}/c/g/${item.img}` : item.extimg"
                                    class="h-auto w-full rounded-lg"
                                    alt=""
                                    loading="lazy"
                                >
                            </picture>

                        </div>
                    </div>
                </div>
            </div>
            
            <MainLeagues />
        </div>
        <FunPlayModal
            ref="modalRef"
            :value="selectedGame"
            @play="handlePlay"
        />
        <Spinner :show="opening" />
    </div>
</template>

<style scoped>
.show-btn {
    @apply text-center flex items-center justify-center gap-1 text-[var(--color-primary4)] hover:text-[var(--color-primary4)] text-sm font-bold
}
.mgc-Chevron {
    mask-image: url('/chevron-right.svg');
    background-color: var(--color-primary4);
    mask-size: 12px 12px;
    width: 12px;
    height: 12px;
}
.mgc-Chevron:hover {
    background-color: var(--color-primary4);
}
.star-icn{
    mask-image: url('/star.svg');
    background-color: var(--color-primary4);
    mask-size: 18px 18px;
    width: 18px;
    height: 18px;  
}
.slide-img{
    object-fit: fill;
    height: 100%;
    width: 100%;
}
.side-cont{
    position: relative;
    width: 100%;
    height: 250px;
    min-height: 250px;
    border: 1px solid var(--banner-border);
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 3px 10px #0006;
    margin-bottom: 30px;   
}
.carousel-img {
  width: 100%;
  height: 150px; 
  object-fit: cover;
  border-radius: 0.5rem;
}

.casino-jackpot-tabs {
  @apply mx-3 mt-3 p-2 rounded-2xl;
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--color-primary1) 35%, transparent),
    color-mix(in srgb, var(--color-primary1) 15%, transparent)
  );
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.12),
    0 8px 30px rgba(0,0,0,0.35);
}

.casino-jackpot-tabs__list {
  @apply flex items-center justify-center gap-2 list-none m-0 p-0;
}

.casino-jackpot-tabs__pill {
  @apply flex items-center gap-2 px-4 py-2 rounded-xl cursor-pointer text-white/80 text-sm font-semibold transition-all duration-200;
  background: color-mix(in srgb, var(--color-primary1) 12%, transparent);
}

.casino-jackpot-tabs__pill:hover {
  background: color-mix(in srgb, var(--color-primary1) 22%, transparent);
}

.casino-jackpot-tabs__pill--active {
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--color-primary1) 90%, white 5%),
    var(--color-primary1)
  );
  color: #fff;
  box-shadow:
    0 6px 18px rgba(0,0,0,0.45),
    inset 0 1px 0 rgba(255,255,255,0.25);
}

.casino-jackpot-tabs__pill--active span {
  font-weight: 700;
}
</style>
